import { HttpClient } from '@angular/common/http';
import { Injectable, EventEmitter, OnDestroy } from '@angular/core';
import { JsonPatchOperation } from '../interfaces/json-patch-operation';
import ListingAOIQueryRequest from '../models/listing-aoiquery-request';
import ListingAOIQueryRequestViewModel from '../models/listing-aoiquery-request-view-model';
import ListingBookingAdminInfoResultViewModel from '../models/listing-booking-admin-info-result-view-model';
import MapFileInfo from '../models/map-file-info';
import WaitingListItemViewModel from '../models/waiting-list-item-view-model';
import { Observable, of as observableOf, Subject, throwError as observableThrowError, BehaviorSubject, of, EMPTY, throwError } from 'rxjs';
import { catchError, map, publishReplay, refCount, tap } from 'rxjs/operators';

import { ActivatedRoute, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import {
  AdminListingDetails,
  AdminListingsSearchModel,
  AdminListingsSearchResultsModel,
  FIPSCounty,
  Listing,
  ListingActivityViewModel,
  ListingEvent,
  ListingLocation,
  ListingSeasonalLeaseSummary,
  ListingViewModel,
} from '../models';
import { DateService } from './date.service';
import { FileDownloadService } from './file-download.service';
import { HttpHelperService } from './http-helper-service.service';
import { LogService } from './log.service';
import { UserService } from './user.service';
import ListingAdmin from '../models/listing-admin';
import { ListingCacheService } from './listing-cache.service';
import CountyStateFIPS from '../models/county-state-fips';
import { ValueService } from './value.service';
import ListingSeasonAvailabilitySummary from '../models/listing-season-availability-summary';
import ListingAdminViewModel from '../models-internal/listing-admin-view-model';
import { IPropertyChange } from '../interfaces/property-change';
import ListingContactData from '../models/listing-contact-data';
import ListingPrototype from '../models-internal/listing-prototype';
import CreateListingFromContactRequest from '../models-internal/create-listing-from-contact-request';
import LeaseRateCardViewModel from '../models/lease-rate-card-view-model';
import BookingTermsConditions from '../models-internal/booking-terms-conditions';
import ListingSeasonalLease from '../models/listing-seasonal-lease';

interface SeasonalRentalAvailabilityResult {
  isAvailable: boolean;
  conflicts?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ListingService implements OnDestroy {
  private readonly _serviceName = 'ListingService';

  /**
   * Private field for verbose logging
   */
  private _verboseLogging = false;

  /**
   * Private subscription for router events
   */
  private routerEvent$: Subscription;

  addedSource = new Subject<Listing>();

  added$ = this.addedSource.asObservable();

  listingViewCounts = new BehaviorSubject(null);

  deletedSource = new Subject<any>();

  deleted$ = this.deletedSource.asObservable();
  listingSeasonalLeaseSummaryCache: ListingSeasonalLeaseSummary[];
  listingSeasonalLeaseSummaryCacheCreate: Date;

  /**
   * An event that is emitted whenever a listing is changed within the application
   */
  public changed = new EventEmitter<Listing | ListingViewModel | ListingAdminViewModel>();

  /**
   * An event that is emitted whenever a listing property is changed within the application
   */
  public propertyChanged = new EventEmitter<IPropertyChange>();

  constructor(
    private http: HttpClient,
    readonly userService: UserService,
    readonly log: LogService,
    readonly dateService: DateService,
    private fileDownloadService: FileDownloadService,
    private httpHelper: HttpHelperService,
    private listingCache: ListingCacheService,
    private router: Router,
    private route: ActivatedRoute,
    private valueService: ValueService
  ) {
    this.subscribeRouterEvents();

    
  }

  initializeViewCounts() {
    this.getListingViewCounts().subscribe({
      next: (data) => {
        this.listingViewCounts.next(data);

        if (this.log.verboseLogging) {
          console.log(`${this._serviceName}().getListingViewCounts`, data);
        }
      },
      error: (err) => {
        console.error(err);
      }
    });
  }
  /**
   * Called once, before the instance is destroyed.
   */
  ngOnDestroy(): void {
    if (this.routerEvent$) {
      this.routerEvent$.unsubscribe();
    }
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging || this._verboseLogging;
  }

  // // get the listings which are closed when I am booked
  // getDependents(listingId: number) {
  //   return this.http.get<Listing[]>(`admin/listings/${listingId}/dependents`);
  // };

  // // get the listings which close my calendar when they are booked
  // getDependencies(listingId: number) {
  //   return this.http.get<Listing[]>(`admin/listings/${listingId}/dependencies`);
  // };

  // (this.listing.id)

  /**
   * Get Bookings
   */
  public getBookings(listingId: number, searchType: string, startDate?: Date, endDate?: Date) {
    if (this.userService.isLoggedIn && this.userService.isAdmin) {
      return this.http.get<ListingBookingAdminInfoResultViewModel>(`admin/listing/${listingId}/bookings/${searchType}`);
    }

    if (this.userService.isLoggedIn) {
      return this.http.get<ListingBookingAdminInfoResultViewModel>(`admin/listing/${listingId}/bookings/${searchType}`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Gets the BookingTypeDisplayConfiguration for a listing
   * @param listingId
   * @returns
   */
  public getBookingTypeDisplayConfiguration(listingId: number) {
    return this.getAsViewModel(listingId).pipe(map((x) => x.bookingTypeDisplayConfiguration));
  }
  /**
   * Gets the Bookings as an Excel Spreadsheet.  This API call is the same as "getBookings".
   * @param listingId
   * @param searchType
   * @param startDate
   * @param endDate
   */
  public downloadBookings(listingId: number, searchType: string, startDate?: Date, endDate?: Date) {
    const url = `admin/listing/${listingId}/bookings/${searchType}/download`;

    return this.fileDownloadService.downloadFile(url);

    // return this.http.get(url, { responseType: ResponseContentType.Blob }).map(
    //   res => {
    //     let contentType = res.headers.get('content-type');
    //     var blob = new Blob([res.blob()], { type: contentType });
    //     return blob;
    //   }
    // );
  }

  getAll() {
    return this.http.get<Listing[]>('listing');
  }

  /**
   * Gets the UTC timestamp of the Listing currentness value.  The API web method
   * does not use caches (value comes from the database)
   */
  public getCurrentness(listingId: number) {
    return this.http.get<Date>(`listing/${listingId}/currentness`);
  }

  /**
   * Sets the Listing currentness value to the current UTC timestamp.
   * @param listingId
   */
  public setListingCurrentess(listingId: number) {
    return this.http.put(`listing/${listingId}/currentness`, null);
  }

  getListings(listingIds: number[]) {
    if (!listingIds) {
      throw new Error('listingIds cannot be null.');
    }

    if (listingIds.length === 0) {
      throw new Error('listingIds array is empty.');
    }

    let parameterString = listingIds.join('&listingId=');

    parameterString = `?listingId=${parameterString}`;

    return this.http.get<ListingViewModel[]>(`v4/listings${parameterString}`);
  }

  getAllByAccountId(userAccountId: number) {
    return this.http.get<Listing[]>(`listing/admin/${userAccountId}`);
  }

  /**
   * Gets a count of listings
   */
  getCount(): Observable<number> {
    if (!this.userService.isLoggedIn) {
      return Observable.create((observer) => {
        observer.next(0);
        observer.complete();
      });
    }

    return this.http.get<number>('listing/Count');
    // return this.http.get('listing/Count')
    //   .map(response => response.text())
    //   .map(Number)
    //   .flatMap(count => Number.isInteger(count)
    //     ? Observable.of(count)
    //     : Observable.throw('Invalid response')
    //   );
  }
  add(item: Listing) {
    return this.http.post<Listing>('listing', item).pipe(tap((listing) => this.addedSource.next(listing)));
  }

  delete(id: number) {
    return this.http.delete(`listing/${id}`).pipe(tap(() => this.deletedSource.next(null)));
  }

  public get(id: number) {
    if (!id) {
      
      return throwError(() => new Error('Empty ID parameter.'));
    }

    let url = `listing/${id}`;

    url = this.appendShareId(url);

    return this.http.get<Listing>(url);
  }

  private appendShareId(url: string): string {
    if (localStorage.getItem('previewShareId')) {
      return `${url}?previewShareId=${localStorage.getItem('previewShareId')}`;
    } else {
      return url;
    }
  }

  getGeneralizedLocation(listingId: number) {
    return this.http.get<ListingLocation>(`listing/${listingId}/generalized`);
  }

  /**
   * Calls the ListingController.Get(int,string)   *
   * @param id Listing ID
   * @returns from '../models/listing-view-model'
   */
  public getAsViewModel(id: number): Observable<ListingViewModel> {
    if (this.log.verboseLogging) console.log(`${this._serviceName}.getAsViewModel for Listing ID:${id} retreived from API.`);

    if (!id) {
      
      return throwError(() => new Error('Empty ID parameter.'));
    }

    let url = `listing/${id}`;

    url = this.appendShareId(url);

    return this.http.get<ListingViewModel>(url).pipe(tap((model) => this.listingCache.addListing(model)));

    /*
    

    const listing = this.listingCache.getListing(id);

    if (listing){      
      console.log(`${this._serviceName}.getAsViewModel for Listing ID:${id} retreived from Listing Cache.`); 
      return of(listing);
    }
    else{
      console.log(`${this._serviceName}.getAsViewModel for Listing ID:${id} retreived from API.`); 
      return this.http.get<ListingViewModel>("listing/" + id)
      .pipe(
        tap(model => this.listingCache.addListing(model)))
        ;
    }
    */
  }

  /**
   * Gets a random listing id for an active and approved listing
   * @returns
   */
  public getRandomListingId() {
    return this.http.get<number>('listing/id/random');
  }

  addDependent(listingId: number, dependentListingId: number) {
    return this.http.put(`listing/${listingId}/dependent/${dependentListingId}`, {});
  }

  removeDependent(listingId: number, dependentListingId: number) {
    return this.http.delete(`listing/${listingId}/dependent/${dependentListingId}`);
  }

  getActivities(listingId: number) {
    return this.http.get<ListingActivityViewModel[]>(`listing/${listingId}/activity`);
  }

  getMinimumPrice(listingId: number) {
    return this.http.get<number>(`listing/${listingId}/getMinimumPrice`);
  }

  addActivity(listingId: number, activityId: number, rules?: string) {
    return this.http.post<ListingActivityViewModel[]>(`listing/${listingId}/activity/${activityId}`, { description: rules });
  }

  updateActivity(listingId: number, activityId: number, rules?: string) {
    return this.http.put<ListingActivityViewModel[]>(`listing/${listingId}/activity/${activityId}`, { description: rules });
  }

  deleteActivity(listingId: number, activityId: number) {
    return this.http.delete(`listing/${listingId}/activity/${activityId}`);
  }

  addActivityOption(listingId: number, activityId: number, activityOptionId: number) {
    return this.http.post<ListingActivityViewModel[]>(`listing/${listingId}/activity/${activityId}/option/${activityOptionId}`, {});
  }

  updateActivityOption(listingId: number, activityId: number, activityOptionId: number) {
    return this.http.put<ListingActivityViewModel[]>(`listing/${listingId}/activity/${activityId}/option/${activityOptionId}`, {});
  }

  deleteActivityOption(listingId: number, activityId: number, activityOptionId: number) {
    return this.http.delete(`listing/${listingId}/activity/${activityId}/option/${activityOptionId}`);
  }

  addEvent(listingId: number, event: ListingEvent) {
    return this.http.post(`admin/listings/${listingId}/event`, event);
  }

  getAdminListing(listingId: number) {
    return this.http.get<AdminListingDetails>(`admin/listings/${listingId}`);
  }

  public getSeasonalRentalsAdminView(listingId: number) {
    return this.http.get<ListingSeasonalLease>(`SeasonalRental/listing/${listingId}/admin`);
  }

  public getSeasonalRental(seasonalRentalId: number) {
    return this.http.get<unknown>(`SeasonalRental/${seasonalRentalId}`);
  }

  public updateSeasonalRental<T extends { listingId: number }>(seasonalRental: T) {
    return this.http.put<unknown>(`SeasonalRental/${seasonalRental.listingId}`, seasonalRental);
  }

  public addSeasonalRental<T>(seasonalRental: T) {
    return this.http.post<unknown>('SeasonalRental', seasonalRental);
  }

  public isSeasonalRentalDateRangeAvailable(listingId: number, startDateTime: Date, endDateTime: Date) {
    return this.http.get<SeasonalRentalAvailabilityResult>(
      `admin/SeasonalRental/available/${listingId}?startDateTime=${startDateTime.toISOString()}&endDateTime=${endDateTime.toISOString()}`
    );
  }

  /**
   * Gets a new ListingAdmin from the API
   * api/listing/0/admin
   * @param listingId
   */
  public getListingAdmin(listingId: number): Observable<ListingAdminViewModel> {
    // api/listing/0/admin

    if (this.userService.isLoggedIn) {
      return this.http.get<ListingAdminViewModel>(`listing/${listingId}/admin`);
    } else {
      return this.http.get<ListingAdminViewModel>(`listing/${listingId}/admin`);
    }
  }

  /**
   * Gets a listing configuration value (typically a single property on ListingAdmin).  Return value is unknown and is usually string,number,boolean.
   * @param listingId
   * @param name
   */
  public getListingConfigValue(listingId: number, name: string): Observable<unknown> {
    return this.http.get<unknown>(`listing/${listingId}/conf/${name}`);
  }

  /**
   * Renders a static listing page (used by facebook share preview)
   * @param listingId
   */
  public renderStatic(listingId: number) {
    return this.http.post(`listing/${listingId}/render/static`, null);
  }

  /**
   * Gets all of the waiting list items for all listings
   */
  getWaitingList() {
    return this.http.get<WaitingListItemViewModel[]>('admin/listings/waiting');
  }

  seasonalLeaseSummary: Observable<ListingSeasonalLeaseSummary[]>;

  /**
   * Gets a summary of seasonal lease information, including if the listing is sold out of all future seasonal
   * leases.
   */
  getSeasonalLeaseSummary() {
    const maxAgeSeconds = 20;

    // Cache it once if configs value is false
    if (!this.seasonalLeaseSummary) {
      this.seasonalLeaseSummary = this.http.get<ListingSeasonalLeaseSummary[]>('listing/seasonallease').pipe(
        map((data) => data),
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      );
    }

    return this.seasonalLeaseSummary;

    // if ( this.listingSeasonalLeaseSummaryCacheCreate && this.listingSeasonalLeaseSummaryCache ){

    //     const expiry = this.dateService.addSeconds(this.listingSeasonalLeaseSummaryCacheCreate , maxAgeSeconds);

    //     if (!this.dateService.isDateGreaterThanOrEqualTo(new Date(), expiry)){
    //       console.log('getSeasonalLeaseSummary return cache');
    //       return Observable.of(this.listingSeasonalLeaseSummaryCache);
    //     }
    // }

    // return this.http.get<ListingSeasonalLeaseSummary[]>(`listing/seasonallease`)
    //       .pipe(map(results => {
    //         console.log('listing/seasonallease pipe->map');
    //         console.log('getSeasonalLeaseSummary set cache');

    //         this.listingSeasonalLeaseSummaryCacheCreate = new Date();
    //         this.listingSeasonalLeaseSummaryCache = results

    //         return results;
    //       }));
  }

  /**
   * Gets the timezone description for a listing view model
   */
  public getTimeZoneDescription(listingViewModel: ListingViewModel): string {
    if (listingViewModel && (listingViewModel as any).timeZoneDescription) {
      return (listingViewModel as any).timeZoneDescription;
    }
    return null;
  }

  public searchAdmin(searchModel: AdminListingsSearchModel) {
    if (!searchModel) {
      throw new Error('searchModel cannot be null/undefined.');
    }

    if (!searchModel.pageSize) {
      throw new Error('Pagesize must be greater than zero.');
    }

    return this.http.post<AdminListingsSearchResultsModel[]>('admin/listings/search', searchModel);
  }

  transfer(listingId: number, emailAddress: string) {
    // expect 204
    return this.http.post('listing/' + listingId + '/transfer', { emailAddress });
  }

  /**
   * Patches a single value for a Listing
   * @param id
   * @param path
   * @param value
   */
  public patchValue(id: number, path: string, value: any) {
    let patchOperation = { path, value, op: 'replace' } as JsonPatchOperation;

    return this.httpHelper.patch(`listing/${id}`, [patchOperation]);
  }

  /**
   * Patches multiple values for a Listing in a single patch operation
   * @param id
   * @param patchDocument
   */
  public patchValues(id: number, patchDocument: JsonPatchOperation[]) {
    return this.httpHelper.patch(`listing/${id}`, patchDocument);
  }

  requestApproval(id: number): Observable<{}> {
    return this.http.get('listing/' + id + '/requestApproval').pipe(
      // expect 204
      catchError((err) => observableThrowError(err))
    );
  }

  /**
   * Clones a listing and returns the listing id of the new, cloned listing
   * @param id
   */
  clone(id: number): Observable<number> {
    const url = `listing/${id}/clone`;

    return this.http.post(url, {}).pipe(
      map((response) => {
        return +response;
      })
    );
  }

  approve(id: number): Observable<{}> {
    return this.http.get('listing/' + id + '/approve').pipe(
      // expect 204
      catchError((err) => observableThrowError(err))
    );
  }

  getDaysAdanceNoticeRequired(_id: /*id*/ number) {
    return observableOf(2);
  }

  activation(id: number, active: boolean, acknowledgement): Observable<{}> {
    const url = `listing/${id}/active/${active}/${acknowledgement}`;
    return this.http.post(url, {}); // expect 204
  }

  getCounties({ state, matchingListingsOnly }: { state: string; matchingListingsOnly: boolean }) {
    const url = `locality/state/${state}/${matchingListingsOnly}`;
    return this.http.get<FIPSCounty[]>(url);
  }

  /**
   * Gets available map files for a listing.  The models returned from
   * this method do NOT contain the actual file.  See downloadMapFile for the actual
   * file.  This method is used by the UpcomingReservationsComponent to determine
   * which map download buttons to show to the user.
   * @param listingId
   */
  getMapFiles(listingId: number) {
    const url = `listing/${listingId}/maps`;
    return this.http.get<MapFileInfo[]>(url);
  }

  /**
   * Downloads Map File
   * @param listingId
   * @param layoutName
   */
  downloadMapFile(listingId: number, layoutName: string) {
    const url = `listing/${listingId}/map/print/${layoutName}`;

    return this.fileDownloadService.downloadFile(url);

    // return this.http.get(url, { responseType: ResponseContentType.Blob }).map(
    //   res => {

    //     let contentType = res.headers.get('content-type') || 'application/pdf';
    //     var blob = new Blob([res.blob()], { type: contentType });

    //     return blob;
    //   }
    // );
  }

  public downloadArrivalInstructionAudioFile(listingId: number) {
    if (this.userService.isLoggedIn) {
      const url = `listing/${listingId}/admin/arrival-instructions/file`;
      const options = { responseType: 'blob' as 'json' };
      return this.http.get(url, options);
    } else {
      const url = `listing/${listingId}/admin/arrival-instructions/file`;
      const options = { responseType: 'blob' as 'json' };
      return this.http.get(url, options);
    }
  }

  public invalidateArrivalInstruction(listingId: number) {
    const url = `listing/${listingId}/admin/arrival-instructions/invalidate`;

    return this.http.get(url);
  }

  // admin/arrival-instructions/invalidate

  /**
   * Gets a boolean as to whether the user has access to a listing.
   * api/listing/12323/has-access
   */
  public hasAccess(listingId: number) {
    const url = `listing/${listingId}/has-access`;

    return this.http.get<boolean>(url);
  }

  public updateMapMarkup(listingId: number, mapMarkup: any) {
    return this.http.post(`listing/${listingId}/mapmarkup`, mapMarkup);
  }

  /**
   * getMapMarkup
   */
  public getBoundaryFeatures(listing: ListingViewModel) {
    if (this.verboseLogging) {
      console.log(`${this._serviceName}.getBoundaryFeatures()`);
    }

    if (listing && listing.mapMarkup) {
      const mapMarkup = JSON.parse(listing.mapMarkup);

      const boundaryFeatures = mapMarkup.features.filter((f) => {
        if (f) {
          if (this.valueService.getSafeValue(f, 'properties.featureType') === 'boundary') {
            return f;
          }
        }
      });

      return boundaryFeatures;
    }

    return null;
  }

  /**
   * Gets the listing event ids that have user AOIs associated with them
   * @param listingId
   */
  public getListingAOIActivatedListingEventIds(listingId: number) {
    return this.http.get<number[]>(`listing/admin/event/${listingId}/user/aoi/ids`);
  }

  /**
   * api/listing/0/aoi/user
   * asdasd
   */
  // public getUserAoiListing(listingId: number) {
  //   return this.http.get<ListingAOIQueryResultViewModel>(`listing/${listingId}/aoi/user`);
  // }

  public getAOIQueryRequests(listingId: number) {
    return this.http.get<ListingAOIQueryRequestViewModel[]>(`listing/${listingId}/aoi`);
  }

  /**
   * Gets lease rate estimate for a Listing
   * api/listing/0/lease-rate-est
   * @param contactId
   * @returns
   */
  public getLeaseRate(listingId: number) {
    return this.http.get<LeaseRateCardViewModel>(`listing/${listingId}/lease-rate-est`);
  }

  /**
   * Gets the listing view model cache comparison.
   * @param listingId
   */
  public getListingViewModelCacheComparison(listingId: number) {
    return this.http.get<any>(`listing/${listingId}/cachecompare`);
  }

  public runListingAOIQuery(listingId: number) {
    let request = { listingId: listingId } as ListingAOIQueryRequest;
    return this.http.post(`listing/${listingId}/aoi`, request);
  }

  /**
   * http://localhost:62265/api/listing/viewCount
   */
  public getListingViewCounts() {
    return this.http.get('listing/viewCount');
  }

  /**
   * Gets the view count for a listing
   * @param listingId
   * @returns
   */
  public getViewCount(listingId: number) {
    if (!listingId) {
      return throwError(() => new Error('listingId parameter is required.'));
    }
    return this.http.get<number>(`listing/${listingId}/viewCount`);
  }

  /**
   * Gets the shareable ical link to the listings booking as an
   * @param listingId
   */
  public getBookingCalendarUrl(listingId: number) {
    return this.http.get<string>(`listing/${listingId}/bookings/url`);
  }

  /**
   * Gets booking status outlook for a listing (per-day status items)
   * Expected API: GET listing/bookingoutlook/{listingId}
   */
  public getBookingStatus(listingId: number) {
    if (!listingId) {
      return throwError(() => new Error('listingId parameter is required.'));
    }

    return this.http.get<any[]>(`listing/bookingoutlook/${listingId}`);
  }

  /**
   * Gets
   * @param listingId
   */
  public getArrivalInstructionAdminUrl(listingId: number) {
    if (!this.userService.isAdmin) return EMPTY;

    return this.http.get<string>(`listing/${listingId}/admin/arrival-instructions/url`);
  }

  /**
   * Gets listing Season availability summary (for hunting)
   */
  public getListingSeasonAvailabilitySummary() {
    if (!this.userService.isAdmin) return EMPTY;

    return this.http.get<ListingSeasonAvailabilitySummary[]>('listing/availability/summary');
  }

  public getListingSeasonAvailabilitySummaryCsv() {
    if (!this.userService.isAdmin) return EMPTY;

    const requestOptions: Object = {
      responseType: 'text',
    };

    return this.http.get<ListingSeasonAvailabilitySummary[]>('listing/availability/summary/csv', requestOptions);
  }

  /**
   * Creates a new listing for a listing contact
   * @param arg
   */
  public createListingFromContact(arg: CreateListingFromContactRequest) {
    const url = 'listing/contact-convert';

    return this.http.post<Listing>(url, arg).pipe(tap((listing) => this.addedSource.next(listing)));
  }

  /**
   * Gets the Booking Terms and Conditions for the listing, which is used to build the member's lease agreement during the booking workflow process
   * @param listingId
   * @param bookingTypeId
   * @returns
   */
  public getBookingTermsConditions(listingId: number, bookingTypeId: number) {
    if (listingId === 0 || listingId === undefined) {
      return observableThrowError(new Error('Listing ID cannot be zero or empty.'));
    }

    if (bookingTypeId === 0 || bookingTypeId === undefined) {
      return observableThrowError(new Error('Booking Type ID cannot be zero or empty.'));
    }

    return this.http.get<BookingTermsConditions>(`listing/${listingId}/membership-agreement/terms/${bookingTypeId}`);
  }

  /**
   * Subscribes to Router Events and sets the _verboseLogging flag
   */
  private subscribeRouterEvents() {
    this.routerEvent$ = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // console.log(`${this._serviceName}.CTOR router.events NavigationEnd:`, event);

        // console.log(
        //   `${this._serviceName}.CTOR router.events NavigationEnd route.snapshot.queryParamMap=`,
        //   this.route.snapshot.queryParamMap
        // );

        if (this.route.snapshot.queryParamMap.has('verboseLogging')) {
          if (this.valueService.getBoolean(this.route.snapshot.queryParamMap.get('verboseLogging')) == true) {
            this._verboseLogging = true;
          } else {
            this._verboseLogging = false;
          }
        }
      }
    });
  }
}

export type ListingPatch<K extends string & keyof Listing> = { path: K; value: Listing[K]; op: 'replace' };
